"use client";

import * as React from "react";
import * as StackedSheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useStackedSheetStore } from "@/stores/stacked-sheet-store";

const StackedSheet = StackedSheetPrimitive.Root;

const StackedSheetTrigger = StackedSheetPrimitive.Trigger;

const StackedSheetClose = StackedSheetPrimitive.Close;

const StackedSheetPortal = StackedSheetPrimitive.Portal;

const StackedSheetOverlay = React.forwardRef<
  React.ElementRef<typeof StackedSheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof StackedSheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <StackedSheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 modal-backdrop data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
));
StackedSheetOverlay.displayName = StackedSheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background shadow-lg transition transition-all duration-500 ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-1/3",
        right:
          "top-2 bottom-2 right-0  w-3/4 rounded-lg border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-[min(900px,70svw)]",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface StackedSheetContentProps
  extends
    React.ComponentPropsWithoutRef<typeof StackedSheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

const StackedSheetContent = React.forwardRef<
  React.ElementRef<typeof StackedSheetPrimitive.Content>,
  StackedSheetContentProps & { open: boolean }
>(({ side = "right", className, children, open, ...props }, ref) => {
  const { removeSheet, addSheet, sheetCount } = useStackedSheetStore();
  const [sheetId, setSheetId] = React.useState(0);
  const [sheetWidth, setSheetWidth] = React.useState(0);
  const sheetRef = React.useRef<HTMLDivElement>(null);

  const getStackedPosition = React.useCallback(
    (width: number, sheetId: number, sheetCount: number) => {
      const rel = sheetCount - sheetId;
      if (sheetId == 0 || rel < 0) {
        return "";
      }
      switch (rel) {
        case 0:
          //the top element
          return "8px ";
        default:
          return `${150 * rel}px`;
      }
    },
    [], // No dependencies needed as it's a pure calculation
  );

  React.useEffect(() => {
    if (open) {
      setSheetId(addSheet());
    } else if (sheetId > 0) {
      removeSheet();
    }
    // do not change the dependencies of this effect -> will cause infinite re-renders
  }, [open]);

  React.useEffect(() => {
    if (!sheetRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSheetWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(sheetRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [sheetRef.current]); // Only re-run if the ref changes

  const position = getStackedPosition(sheetWidth, sheetId, sheetCount);

  return (
    <StackedSheetPortal>
      <StackedSheetOverlay />
      <StackedSheetPrimitive.Content
        ref={(node) => {
          // Handle both refs
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
          if (sheetRef) sheetRef.current = node;
        }}
        className={cn(sheetVariants({ side }), className)}
        style={{ right: position }}
        {...props}
      >
        {children}
        <StackedSheetPrimitive.Close className="absolute right-6 top-6 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </StackedSheetPrimitive.Close>
      </StackedSheetPrimitive.Content>
    </StackedSheetPortal>
  );
});
StackedSheetContent.displayName = StackedSheetPrimitive.Content.displayName;

const StackedSheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-2 text-center sm:text-left", className)} {...props} />
);
StackedSheetHeader.displayName = "StackedSheetHeader";

const StackedSheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props}
  />
);
StackedSheetFooter.displayName = "StackedSheetFooter";

const StackedSheetTitle = React.forwardRef<
  React.ElementRef<typeof StackedSheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof StackedSheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <StackedSheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
));
StackedSheetTitle.displayName = StackedSheetPrimitive.Title.displayName;

const StackedSheetDescription = React.forwardRef<
  React.ElementRef<typeof StackedSheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof StackedSheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <StackedSheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
StackedSheetDescription.displayName = StackedSheetPrimitive.Description.displayName;

export {
  StackedSheet,
  StackedSheetPortal,
  StackedSheetOverlay,
  StackedSheetTrigger,
  StackedSheetClose,
  StackedSheetContent,
  StackedSheetHeader,
  StackedSheetFooter,
  StackedSheetTitle,
  StackedSheetDescription,
};
